import React, { useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import FilterSelect from "components/Form/filterSelect";
import { toast, Bounce } from "react-toastify";

import { useSelector } from "react-redux";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Button } from "components/Button";
import { format } from "date-fns";
import { createResginationApi } from "../../api/services/resignations";
import EssFullWidthModal from "components/modelpopup/baseModal";
import useUploadHook from "components/uploadHook";
import { useDropzone } from "react-dropzone";
import ViewResignationAttachment from "views/pages/Resignation/resignationDetail/attachmentHandler";

const TOAST = { position: "top-right", autoClose: 5000, transition: Bounce };

const resignRequestSchema = Yup.object().shape({
  reason: Yup.string().required("Reason is required"),
  last_working_day: Yup.string().required("Last Working Date is required"),
  company_user: Yup.object().required("User is required"),
  attachments: Yup.array().of(
    Yup.object().shape({
      url: Yup.string().url("Invalid URL").required("URL is required"),
    })
  ),
});

const toOption = (person) => ({ label: `${person.first_name} ${person.last_name}`, value: person.id });

/** Apply-for-resignation form. Viewing and deciding on an existing request
 *  lives in Resignation/components/ResignationDetailPanel. */
const AllResignationAddPopup = ({
  resignationModal,
  setResignationModal,
  loading,
  setLoading,
  setSelectedResignation,
  getResignationList,
}) => {
  const { user } = useSelector((state) => state.user);
  const { userData, userDataLoading } = useSelector((state) => state.common);
  const { uploadLoading, handleFile } = useUploadHook();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    reset,
  } = useForm({
    resolver: yupResolver(resignRequestSchema),
    defaultValues: { reason: "", last_working_day: null, company_user: null, attachments: [] },
  });

  const { fields, remove } = useFieldArray({ control, name: "attachments" });

  // The employee applies for themselves; the user field is shown, not editable.
  useEffect(() => {
    if (!resignationModal) return;
    reset({ reason: "", last_working_day: new Date(), company_user: null, attachments: [] });
    const self = (userData || []).find((item) => item.id === user?.id);
    if (self) setValue("company_user", toOption(self));
  }, [resignationModal, userData, user?.id, reset, setValue]);

  const close = () => {
    setResignationModal(false);
    setSelectedResignation("");
    reset();
  };

  const fileUploadHandler = async (acceptedFiles, rejectedFiles, e) => {
    const urls = await handleFile(e);
    const uploaded = (Array.isArray(urls) ? urls : [urls]).filter(Boolean).map((url) => ({ url }));
    setValue("attachments", [...fields, ...uploaded]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: fileUploadHandler,
    accept: "image/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });

  const showFieldErrors = (e) => {
    const data = e?.response?.data;
    if (data && typeof data === "object" && !data.error) {
      Object.entries(data).forEach(([k, v]) => setError(k, { type: "manual", message: [].concat(v).join(", ") }));
      return;
    }
    toast.error(data?.error || "Failed to submit the resignation.", TOAST);
  };

  const onSubmit = (fd) => {
    const data = {
      ...fd,
      company_user: fd?.company_user?.value,
      last_working_day: fd.last_working_day ? format(new Date(fd.last_working_day), "yyyy-MM-dd") : fd.last_working_day,
      attachments: (fd?.attachments || []).map((item) => ({ id: item.item_id || "", url: item.url })),
    };
    setLoading(true);
    createResginationApi(data)
      .then(() => {
        toast.success("Resignation submitted.", TOAST);
        close();
        getResignationList();
      })
      .catch(showFieldErrors)
      .finally(() => setLoading(false));
  };

  return (
    <div role="dialog">
      <EssFullWidthModal
        mask={false}
        title="Apply Resignation"
        open={resignationModal}
        width={"40%"}
        footer={[]}
        onCancel={close}
        className="full-width-modal"
      >
        <div className="modal-content">
          <div className="modal-body">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <div className="col-md-6">
                  <div className="input-block mb-3">
                    <label className="col-form-label">User</label>
                    <Controller
                      name="company_user"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <FilterSelect
                          onChange={onChange}
                          optionLabel={"label"}
                          optionValue={"value"}
                          options={(userData || []).map(toOption)}
                          isDisabled={true}
                          isLoading={userDataLoading}
                          value={value}
                          placeholder={"Company user"}
                        />
                      )}
                    />
                    {errors.company_user && <small className="text-danger">{errors.company_user?.message}</small>}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      Last Working Day <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="last_working_day"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          className="form-control floating datetimepicker"
                          selected={field?.value}
                          onChange={field.onChange}
                          dateFormat="dd-MM-yyyy"
                        />
                      )}
                    />
                    {errors.last_working_day && <small className="text-danger">{errors.last_working_day?.message}</small>}
                  </div>
                </div>
              </div>

              <div className="input-block mb-3">
                <label className="col-form-label" htmlFor="resignation-reason">
                  Reason <span className="text-danger">*</span>
                </label>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <textarea id="resignation-reason" rows={6} className="form-control" value={field?.value} onChange={field?.onChange} />
                  )}
                />
                {errors.reason && <small className="text-danger">{errors.reason?.message}</small>}
              </div>

              <div className="input-block mb-3">
                <label className="col-form-label">
                  Attachments {uploadLoading && <i className="fa fa-spinner fa-spin" aria-hidden="true" />}
                </label>
                <div
                  {...getRootProps({ className: "dropzone" })}
                  style={{ border: "1.5px dashed #C4C8CF", borderRadius: 12, padding: 20, textAlign: "center", cursor: "pointer", color: "#6B7280" }}
                >
                  <input {...getInputProps()} />
                  <p className="mb-0">Drag & drop files here, or click to choose</p>
                </div>
                <p className="text-danger mb-2">{errors?.attachments?.message}</p>
              </div>

              <ViewResignationAttachment fields={fields} remove={remove} />

              <div className="submit-section">
                <Button loading={loading} disabled={loading} className="btn btn-primary submit-btn" type="submit" label="Submit" />
              </div>
            </form>
          </div>
        </div>
      </EssFullWidthModal>
    </div>
  );
};

export default AllResignationAddPopup;
